import { ScreenShareIcon } from "@minervaproject/fds";
import { UAParser } from "ua-parser-js";

import { useObjectSelector } from "hooks/redux_hooks";
import {
  EnterClassMonitoringEventType,
  MonitoringEventTypeType,
} from "shared/types/monitoring_event";
import { PreclassTestNameType } from "shared/types/preclass_check";
import {
  selectLatestMonitoringEventByUserIdAndType,
  selectLatestPreClassCheckResultsByUserId,
} from "store/selectors/monitoring";

// https://help.minervaproject.com/en/articles/4292732-computer-system-requirements
const MIN_CPU_CORES = 4;
const MIN_MEMORY = 8;
const MIN_GEEKBENCH_SCORE = 1650;

function UserComputer({ userId }: { userId: string }) {
  const latestPreClassCheckResults = useObjectSelector(
    selectLatestPreClassCheckResultsByUserId(userId),
    ["eventId"],
  );

  const cpuScoreResult = latestPreClassCheckResults?.tests.find(
    ({ name }) => name === PreclassTestNameType.CPU_CORES,
  );
  const cpuModel = cpuScoreResult?.data?.stats?.modelName;
  const geekbenchMultiScore = cpuScoreResult?.data?.stats?.geekbench?.["multi-score"];

  const cpuCoresResult = latestPreClassCheckResults?.tests.find(
    ({ name }) => name === PreclassTestNameType.CPU_CORES,
  );
  const cpuCores = cpuCoresResult?.data?.cpus;

  const totalMemoryResult = latestPreClassCheckResults?.tests.find(
    ({ name }) => name === PreclassTestNameType.TOTAL_MEMORY,
  );
  const memory = totalMemoryResult?.data?.memory;

  const latestEnterClassEvent = useObjectSelector(
    selectLatestMonitoringEventByUserIdAndType(userId, MonitoringEventTypeType.ENTER_CLASS),
    ["id"],
  ) as EnterClassMonitoringEventType | undefined;
  const { ipAddress, userAgent } = latestEnterClassEvent?.contextData || {};
  const parser = userAgent && new UAParser(userAgent);
  const parserResults = parser && parser.getResult();
  const { browser, os } = parserResults || {};

  const errorStyle = {
    backgroundColor: "var(--red-tint-90)",
    color: "var(--red)",
  };

  if (!latestPreClassCheckResults) {
    return (
      <div data-testid="user-computer">
        <h2 className="h2">Computer</h2>
        <div className="my3 body-s">
          The user has not performed any pre-class checks or system checks.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="user-computer">
      <h2 className="h2 mxn6">
        <ScreenShareIcon width={16} height={16} />
        <span className="ml2">Computer</span>
      </h2>
      <table className="fds-table">
        <thead>
          <tr>
            <th>Element</th>
            <th>User Value / Requirements</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>CPU Model</td>
            <td>{cpuModel || "Unknown"}</td>
          </tr>
          <tr style={cpuCores && cpuCores < MIN_CPU_CORES ? errorStyle : undefined}>
            <td>CPU Cores</td>
            <td>
              {cpuCores || "Unknown"} / {MIN_CPU_CORES}
            </td>
          </tr>
          <tr style={memory && Math.ceil(memory) < MIN_MEMORY ? errorStyle : undefined}>
            <td>Memory</td>
            <td>
              {(memory && Math.ceil(memory)) || "Unknown"} GB / {MIN_MEMORY} GB
            </td>
          </tr>
          <tr
            style={
              geekbenchMultiScore && geekbenchMultiScore < MIN_GEEKBENCH_SCORE
                ? errorStyle
                : undefined
            }
          >
            <td>Geekbench</td>
            <td>
              {geekbenchMultiScore || "Unknown"} / {MIN_GEEKBENCH_SCORE}
            </td>
          </tr>
          <tr>
            <td>IP</td>
            <td>{ipAddress || "-"}</td>
          </tr>
          <tr>
            <td>Platform</td>
            <td>{browser ? `${browser.name} ${browser.version}` : "-"}</td>
          </tr>
          <tr>
            <td>OS</td>
            <td>{os ? `${os.name} ${os.version}` : "-"}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export { UserComputer };
